import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Building2, Loader2, X, CreditCard, RefreshCw, Sparkles, Crown } from 'lucide-react';
import SharedHeader from './SharedHeader';

const PricingPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [razorpayConfigured, setRazorpayConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [checkoutModal, setCheckoutModal] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const resp = await fetch(`${baseUrl}/api/billing/plans`);
      const data = await resp.json();
      setPlans(data.plans || []);
      setRazorpayConfigured(data.razorpay_configured);
    } catch (err) {
      setError('Could not load pricing plans.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (plan, billingType) => {
    setError('');
    setProcessing(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const cookies = document.cookie.split(';').map(c => c.trim());
      const sessionCookie = cookies.find(c => c.startsWith('session_token='));
      const token = sessionCookie ? sessionCookie.split('=')[1] : '';

      const resp = await fetch(`${baseUrl}/api/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ plan, billing_type: billingType }),
      });

      const data = await resp.json();
      if (!resp.ok) { setError(data.detail || 'Failed to create checkout session'); setProcessing(false); return; }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        const options = {
          key: data.key_id, amount: data.amount, currency: data.currency,
          name: data.name, description: data.description,
          prefill: { name: data.user_name || '', email: data.user_email || '' },
          handler: async (response) => {
            try {
              const verifyResp = await fetch(`${baseUrl}/api/billing/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                body: JSON.stringify({
                  type: data.type, plan,
                  razorpay_subscription_id: response.razorpay_subscription_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              });
              const verifyData = await verifyResp.json();
              if (verifyResp.ok) { setCheckoutModal(null); window.location.href = '/dashboard'; }
              else { setError(verifyData.detail || 'Payment verification failed'); }
            } catch (err) { setError('Could not verify payment. Please contact support.'); }
            setProcessing(false);
          },
          modal: { ondismiss: () => { setProcessing(false); setCheckoutModal(null); } },
          theme: { color: '#2563eb' },
        };
        if (data.type === 'subscription') { options.subscription_id = data.subscription_id; delete options.amount; delete options.currency; }
        else { options.order_id = data.order_id; }
        const rzp = new window.Razorpay(options);
        rzp.open();
      };
      document.body.appendChild(script);
    } catch (err) { setError('Could not start checkout. Please try again.'); setProcessing(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a1525' }}>
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  // Sort plans: free, pro, enterprise
  const sortedPlans = [...plans].sort((a, b) => {
    const order = { free: 0, pro: 1, enterprise: 2 };
    return (order[a.plan] ?? 99) - (order[b.plan] ?? 99);
  });

  const planIcon = (planKey) => {
    if (planKey === 'free') return <Sparkles className="w-5 h-5" />;
    if (planKey === 'pro') return <Zap className="w-5 h-5" />;
    return <Crown className="w-5 h-5" />;
  };

  const planIconBg = (planKey) => {
    if (planKey === 'free') return 'bg-gradient-to-br from-cyan-500 to-blue-500';
    if (planKey === 'pro') return 'bg-gradient-to-br from-blue-500 to-indigo-600';
    return 'bg-gradient-to-br from-amber-500 to-orange-600';
  };

  const planFeatures = (planKey) => {
    if (planKey === 'free') return [
      '250 AI credits (10-day trial)',
      'Basic AI models',
      'Elwave view-only access',
      'Limited Pulse simulations',
      'No credit card required',
    ];
    if (planKey === 'pro') return [
      '500 AI requests / month',
      'Specialized AI models',
      'Full Elwave + AI chat',
      'Unlimited Pulse simulations',
      'elip IP generation',
      'Coverage analyzer',
    ];
    return [
      'Custom credit limits',
      'On-prem license server',
      'SSO (SAML / OIDC)',
      'Audit logs & compliance',
      'Dedicated support',
      'Specialized AI models',
    ];
  };

  return (
    <div className="min-h-screen" style={{ background: '#0a1525' }}>
      <SharedHeader activePage="pricing" />

      {/* ─── Pricing Content ─── */}
      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-3">
              Simple, transparent pricing
            </h1>
            <p className="text-slate-400 text-lg">
              Start free for 10 days. Upgrade when you're ready. Cancel anytime.
            </p>
          </div>

          {/* Plans — 3 columns */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {sortedPlans.map((p) => {
              const isFree = p.plan === 'free';
              const isEnterprise = p.plan === 'enterprise';
              const isPro = p.plan === 'pro';
              const isSelected = selectedPlan === p.plan;

              return (
                <div
                  key={p.plan}
                  onClick={() => setSelectedPlan(p.plan)}
                  className={`relative bg-slate-900/80 backdrop-blur-md rounded-2xl border p-7 flex flex-col cursor-pointer transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 ring-2 ring-blue-500/40'
                      : 'border-slate-700/50 hover:border-slate-600'
                  }`}
                >
                  {/* Plan header */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white ${planIconBg(p.plan)}`}>
                      {planIcon(p.plan)}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{p.name}</h3>
                      {isFree && <p className="text-xs text-cyan-400">10-day trial</p>}
                      {isEnterprise && <p className="text-xs text-amber-400">Contact us</p>}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    {isEnterprise ? (
                      <span className="text-4xl font-bold text-white">Custom</span>
                    ) : isFree ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-white">Free</span>
                        <span className="text-slate-500 text-sm">/ 10 days</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-white">{p.amount_display}</span>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-slate-400 text-sm mb-5">{p.description}</p>

                  {/* Divider */}
                  <div className="border-t border-slate-700/50 mb-5" />

                  {/* Features */}
                  <div className="flex-1 space-y-3 mb-7">
                    {planFeatures(p.plan).map((feat, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        <div className="w-5 h-5 rounded-full bg-green-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-green-400" />
                        </div>
                        <span className="text-slate-300 text-sm">{feat}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  {isFree ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate('/dashboard'); }}
                      className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors cursor-pointer"
                      style={{ border: 'none' }}
                    >
                      Start Free Trial
                    </button>
                  ) : isEnterprise ? (
                    <button
                      onClick={() => navigate('/?section=contact')}
                      className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors cursor-pointer"
                      style={{ border: 'none' }}
                    >
                      Contact Sales
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setCheckoutModal({ plan: p.plan, billing_type: 'subscription' }); }}
                        disabled={!razorpayConfigured || processing}
                        className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                          isSelected
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                        }`}
                        style={{ border: 'none' }}
                      >
                        <RefreshCw className="w-4 h-4" />
                        Auto-pay Monthly
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setCheckoutModal({ plan: p.plan, billing_type: 'one_time' }); }}
                        disabled={!razorpayConfigured || processing}
                        className={`w-full py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                          isSelected
                            ? 'bg-slate-700 hover:bg-slate-600 text-white'
                            : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                        }`}
                        style={{ border: 'none' }}
                      >
                        <CreditCard className="w-4 h-4" />
                        Pay Once
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!razorpayConfigured && (
            <p className="text-center text-slate-500 text-sm mt-8">
              Payments are being set up. Please check back soon.
            </p>
          )}
        </div>
      </div>

      {/* ─── Checkout Modal ─── */}
      {checkoutModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => !processing && setCheckoutModal(null)}
        >
          <div
            className="w-full max-w-md bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-white">Confirm Checkout</h2>
              {!processing && (
                <button onClick={() => setCheckoutModal(null)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-slate-300">
                <span>Plan</span>
                <span className="font-medium capitalize">{checkoutModal.plan}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Billing</span>
                <span className="font-medium">
                  {checkoutModal.billing_type === 'subscription' ? 'Auto-pay monthly' : 'One-time payment'}
                </span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Amount</span>
                <span className="font-medium">
                  {plans.find(p => p.plan === checkoutModal.plan)?.amount_display}
                </span>
              </div>
            </div>

            {error && (
              <div className="mb-4 bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              onClick={() => handleCheckout(checkoutModal.plan, checkoutModal.billing_type)}
              disabled={processing}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              style={{ border: 'none' }}
            >
              {processing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
              ) : (
                <><CreditCard className="w-4 h-4" /> Pay with Razorpay</>
              )}
            </button>

            <p className="text-xs text-slate-500 text-center mt-4">
              You'll be redirected to Razorpay's secure payment page.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
